// Number Bonds Pop: bubbles drift; tap the one that completes the bond. No timer, no
// bubble ever leaves before she has had a fair look.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { makeBond, type BondProblem } from '@problems/number-bonds';

const PROBLEMS = 10;
const COLORS = ['#e2574c', '#f08a3c', '#f5c542', '#5fbf6a', '#3bb3a9', '#4f8fe6', '#9b6be6', '#ee86b7'];

interface Bubble { label: string; x: number; y: number; r: number; vx: number; vy: number; phase: number; color: string; pop: number; shake: number }

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let prob!: BondProblem;
  let bubbles: Bubble[] = [];
  let misses = 0;
  let hinted = false;
  let attempts = 0, correct = 0;
  let level = ctx.currentLevel();
  let maxLevel = level;
  let locked = false;
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Bond ', h('strong', null, '1'), ` of ${PROBLEMS}`), h('span.muted.small', null, ''));
  const prompt = h('div.prompt', { style: { fontSize: '30px' } });
  const stage = h('div');
  const help = h('p.center.muted.small', null, 'Tap the bubble that finishes the number sentence.');
  host.append(head, prompt, stage, help);

  const S = makeSurface(stage, { aspect: 0.95, onResize: () => layout() });

  function newProblem() {
    level = ctx.currentLevel();
    maxLevel = Math.max(maxLevel, level);
    prob = makeBond(level, ctx.rng);
    misses = 0; hinted = false; locked = false;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (head.lastElementChild as HTMLElement).textContent = prob.kind === 'fraction' ? 'Make one whole' : prob.kind === 'decimal' ? 'Make 1' : `Make ${prob.target}`;
    prompt.textContent = `${prob.known} + ? = ${prob.target}`;
    layout();
  }

  function layout() {
    if (!prob || !S.w) return;
    const W = S.w, H = S.h;
    const r = Math.max(26, Math.min(W / 7, 40));
    bubbles = prob.choices.map((label, i) => {
      const cols = 3, row = Math.floor(i / cols), col = i % cols;
      const x = W * (0.2 + col * 0.3) + (row % 2 ? r * 0.4 : -r * 0.4);
      const y = H * (0.3 + row * 0.38);
      return { label, x, y, r, vx: (ctx.rng() - 0.5) * 12, vy: (ctx.rng() - 0.5) * 8, phase: ctx.rng() * 6.28, color: COLORS[(i * 3 + n) % COLORS.length], pop: 0, shake: 0 };
    });
  }

  function update(dt: number) {
    const W = S.w, H = S.h;
    for (const b of bubbles) {
      b.phase += dt;
      b.x += b.vx * dt; b.y += b.vy * dt;
      if (b.x < b.r + 4 || b.x > W - b.r - 4) b.vx *= -1;
      if (b.y < b.r + 4 || b.y > H - b.r - 4) b.vy *= -1;
      b.x = Math.max(b.r + 4, Math.min(W - b.r - 4, b.x));
      b.y = Math.max(b.r + 4, Math.min(H - b.r - 4, b.y));
      if (b.pop > 0) b.pop = Math.max(0, b.pop - dt * 3);
      if (b.shake > 0) b.shake = Math.max(0, b.shake - dt * 4);
    }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#e6f4fb'; c.fillRect(0, 0, W, H);
    for (const b of bubbles) {
      const bob = Math.sin(b.phase * 2) * 3;
      const sx = b.shake > 0 ? Math.sin(b.shake * 40) * 6 : 0;
      const scale = b.pop > 0 ? 1 + b.pop * 0.6 : 1;
      c.globalAlpha = b.pop > 0 ? b.pop : 1;
      c.fillStyle = b.color;
      c.beginPath(); c.arc(b.x + sx, b.y + bob, b.r * scale, 0, Math.PI * 2); c.fill();
      c.fillStyle = 'rgba(255,255,255,.35)';
      c.beginPath(); c.arc(b.x + sx - b.r * 0.35, b.y + bob - b.r * 0.35, b.r * 0.22, 0, Math.PI * 2); c.fill();
      c.fillStyle = '#fff';
      c.font = `900 ${Math.round(b.r * (b.label.length > 4 ? 0.55 : b.label.length > 2 ? 0.7 : 0.9))}px Nunito, sans-serif`;
      c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(b.label, b.x + sx, b.y + bob + 1);
      c.globalAlpha = 1;
    }
  }

  const loop = makeLoop(update, draw);
  loop.start();

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      if (locked) return;
      let hit: Bubble | null = null;
      for (const b of bubbles) if (Math.hypot(p.x - b.x, p.y - b.y) <= b.r + 8) { hit = b; break; }
      if (!hit) return;
      const ms = performance.now() - tProblem;
      if (hit.label === prob.answer) {
        locked = true;
        attempts++; correct++;
        ctx.report({ skill: prob.skill, level, correct: true, hintUsed: hinted, ms, problemId: `bond:${prob.known}+${prob.answer}` });
        ctx.sound('ding');
        hit.pop = 1;
        ctx.say(`${prob.known} + ${prob.answer} = ${prob.target}. Pop!`);
        n++;
        if (n >= PROBLEMS) {
          if (attempts === correct && maxLevel >= 5) ctx.charm('charm.bonds100');
          setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 700);
          return;
        }
        setTimeout(newProblem, 650);
      } else {
        misses++;
        if (misses === 1) { attempts++; ctx.report({ skill: prob.skill, level, correct: false, hintUsed: hinted, ms, problemId: `bond:${prob.known}+${prob.answer}` }); }
        ctx.sound('buzz');
        hit.shake = 1;
        if (misses >= 2) { hinted = true; help.textContent = `Hint: ${prob.hint}`; ctx.say(`${prob.known} + ${hit.label} is not ${prob.target}.`); }
        else ctx.say(`${prob.known} + ${hit.label} is not ${prob.target}. Try another bubble.`);
      }
    },
  });

  newProblem();
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.number-bonds',
  title: 'Number Bonds Pop',
  blurb: 'Pop the bubble that finishes the number sentence. Tens, hundreds, then fractions.',
  category: 'math',
  skills: ['add_sub', 'fractions'],
  levelRange: [1, 10],
  unlock: { stars: 2 },
  mount,
};
export default mod;
